/**
 * Product2 Set Flavor Selection
 * クリーンアーキテクチャに基づいたセット商品フレーバー選択機能
 * Vanilla JavaScript実装
 *
 * アーキテクチャ構成:
 * - Domain Layer: ビジネスロジック（セット数からフレーバー数を決定）
 * - Use Case Layer: ユースケース（フレーバー選択、リセット、自動選択）
 * - Interface Adapter Layer: DOM操作、イベントハンドリング
 * - Framework Layer: Vanilla JavaScript、イベントリスナー
 */

(function(window) {
  'use strict';

  // ===============================================
  // Domain Layer - ビジネスロジック
  // ===============================================

  /**
   * セット数のドメインモデル
   */
  const SetQuantity = {
    /**
     * セット数からフレーバー選択数を取得
     * @param {string} setLabel - セットのラベル（例: "3袋【300円OFF+送料無料】"）
     * @returns {number} - 必要なフレーバー選択数
     */
    getFlavorCount: function(setLabel) {
      if (!setLabel) return 1;

      // ラベルから数値を抽出（例: "3袋" → 3）
      const match = setLabel.match(/^(\d+)/);
      if (match && match[1]) {
        const count = parseInt(match[1], 10);
        return isNaN(count) ? 1 : count;
      }

      return 1; // デフォルトは1つ
    },

    /**
     * フレーバー選択が必要な範囲を取得
     * @param {number} count - セット数
     * @returns {Array<number>} - 表示すべきフレーバーセットのインデックス配列
     */
    getRequiredFlavorSets: function(count) {
      const sets = [];
      // 2つ目から指定された数まで
      for (let i = 2; i <= count; i++) {
        sets.push(i);
      }
      return sets;
    },

    /**
     * 非表示にすべきフレーバーセットを取得
     * @param {number} count - 現在のセット数
     * @param {number} maxCount - 最大セット数（デフォルト12）
     * @returns {Array<number>} - 非表示にすべきフレーバーセットのインデックス配列
     */
    getHiddenFlavorSets: function(count, maxCount = 12) {
      const sets = [];
      for (let i = count + 1; i <= maxCount; i++) {
        sets.push(i);
      }
      return sets;
    }
  };

  /**
   * 在庫マトリクスのドメインモデル
   * data-flavor-availability（[{set, flavor, available}]）から
   * 「セット値 × フレーバー値 → 在庫有無」を引けるようにする。
   */
  const FlavorAvailability = {
    _map: null, // { setValue: { flavorValue: boolean } }

    /**
     * data-flavor-availability を読み込んでマップ化（初回のみ）
     * @returns {Object}
     */
    load: function() {
      if (this._map) return this._map;

      this._map = {};
      const el = document.querySelector('[data-flavor-availability]');
      if (!el) {
        console.log('[FlavorAvailability] data-flavor-availability not found');
        return this._map;
      }

      try {
        const rows = JSON.parse(el.textContent);
        rows.forEach((row) => {
          const setKey = (row.set || '').trim();
          const flavorKey = (row.flavor || '').trim();
          if (!this._map[setKey]) this._map[setKey] = {};
          this._map[setKey][flavorKey] = row.available === true;
        });
        console.log('[FlavorAvailability] Loaded matrix:', this._map);
      } catch (e) {
        console.error('[FlavorAvailability] parse error', e);
      }
      return this._map;
    },

    /**
     * 指定セット × フレーバーが購入可能か判定。
     * マトリクスに無い組み合わせは「制限しない（true）」を返す。
     * @param {string} setValue
     * @param {string} flavorValue
     * @returns {boolean}
     */
    isAvailable: function(setValue, flavorValue) {
      const map = this.load();
      const set = (setValue || '').trim();
      const flavor = (flavorValue || '').trim();
      if (!map[set]) return true;
      if (typeof map[set][flavor] !== 'boolean') return true;
      return map[set][flavor];
    }
  };

  /**
   * フレーバー選択のドメインモデル
   */
  const FlavorSelection = {
    /**
     * 有効な（選択可能な）フレーバーのみをフィルタリング
     * 【修正】is-disabledクラスとdisabled属性の両方をチェック
     * @param {NodeList|Array} flavorItems - フレーバーアイテムの配列
     * @returns {Array<HTMLElement>} - 有効なフレーバーアイテム
     */
    getAvailableFlavors: function(flavorItems) {
      return Array.from(flavorItems).filter(item => {
        const radio = item.querySelector('input[type="radio"]');
        const flavorName = radio ? radio.value : 'unknown';

        // is-disabledクラスのチェック
        if (item.classList.contains('is-disabled')) {
          console.log(`[FlavorSelection] "${flavorName}": Disabled by is-disabled class`);
          return false;
        }

        // data-available属性のチェック（Liquid側で設定）
        const dataAvailable = item.getAttribute('data-available');
        if (dataAvailable === 'false') {
          console.log(`[FlavorSelection] "${flavorName}": Disabled by data-available=false`);
          return false;
        }

        // input要素のdisabled属性のチェック
        if (radio && radio.disabled) {
          console.log(`[FlavorSelection] "${flavorName}": Disabled by input disabled attribute`);
          return false;
        }

        console.log(`[FlavorSelection] "${flavorName}": Available ✓`);
        return true;
      });
    },

    /**
     * フレーバーが全て売り切れかチェック
     * @param {NodeList|Array} flavorItems - フレーバーアイテムの配列
     * @returns {boolean}
     */
    isAllSoldOut: function(flavorItems) {
      return this.getAvailableFlavors(flavorItems).length === 0;
    }
  };

  // ===============================================
  // Use Case Layer - ユースケース
  // ===============================================

  /**
   * フレーバーセット表示ユースケース
   */
  const ShowFlavorSetsUseCase = {
    /**
     * 指定された数のフレーバーセットを表示
     * @param {number} count - 表示するセット数
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(count, repository) {
      // 表示すべきセットを取得
      const requiredSets = SetQuantity.getRequiredFlavorSets(count);
      const hiddenSets = SetQuantity.getHiddenFlavorSets(count);

      // 表示
      requiredSets.forEach(setIndex => {
        repository.show(setIndex);
      });

      // 非表示
      hiddenSets.forEach(setIndex => {
        repository.hide(setIndex);
      });

      console.log(`[ShowFlavorSets] Displaying ${count} sets (2-${count})`);
    }
  };

  /**
   * フレーバー自動選択ユースケース
   */
  const AutoSelectFlavorUseCase = {
    /**
     * フレーバーセット内の最初の有効なフレーバーを自動選択
     * @param {number} setIndex - フレーバーセットのインデックス
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(setIndex, repository) {
      const flavorSet = repository.getFlavorSet(setIndex);
      if (!flavorSet || !this._isVisible(flavorSet)) {
        return;
      }

      const flavorItems = repository.getFlavorItems(flavorSet);
      const availableItems = FlavorSelection.getAvailableFlavors(flavorItems);

      if (availableItems.length > 0) {
        // 最初の有効なフレーバーを選択
        const firstRadio = availableItems[0].querySelector('input[type="radio"]');
        if (firstRadio) {
          firstRadio.checked = true;
          console.log(`[AutoSelect] Set ${setIndex}: Selected first available flavor`);
        }
      } else {
        // 全て売り切れの場合
        this._handleAllSoldOut(flavorSet);
      }
    },

    _isVisible: function(element) {
      return !element.classList.contains('product-form__flavor-set--hidden') &&
             element.offsetParent !== null;
    },

    _handleAllSoldOut: function(flavorSet) {
      const list = flavorSet.querySelector('.block-swatch-list--flavor');
    }
  };

  /**
   * フレーバー選択リセットユースケース
   */
  const ResetFlavorSelectionsUseCase = {
    /**
     * 指定されたセット以降のフレーバー選択をリセット
     * @param {number} fromSet - この番号以降のセットをリセット
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(fromSet, repository) {
      for (let i = fromSet; i <= 12; i++) {
        const flavorSet = repository.getFlavorSet(i);
        if (flavorSet) {
          const radios = flavorSet.querySelectorAll('input[type="radio"]');
          radios.forEach(radio => {
            radio.checked = false;
          });
          console.log(`[Reset] Set ${i}: Unchecked all flavors`);
        }
      }
    }
  };

  /**
   * 在庫による無効化適用ユースケース
   * 選択中のセット値に対し、売り切れフレーバーを
   *   1つ目（variant-picker内のoption2スウォッチ）
   *   2〜12つ目（property スウォッチ）
   * の両方で無効化する。
   */
  const ApplyAvailabilityUseCase = {
    _autoSwitching: false,

    /**
     * スウォッチからフレーバー名を取得（ラベルテキスト優先）
     * @param {HTMLElement} swatch
     * @returns {string}
     */
    _getFlavorName: function(swatch) {
      const label = swatch.querySelector('.block-swatch__label');
      if (label && label.textContent.trim()) return label.textContent.trim();
      const radio = swatch.querySelector('input[type="radio"]');
      return radio ? (radio.value || '').trim() : '';
    },

    /**
     * 1スウォッチの有効/無効状態を更新
     * @param {HTMLElement} swatch
     * @param {boolean} available
     */
    _applyToSwatch: function(swatch, available) {
      const radio = swatch.querySelector('input[type="radio"]');
      if (available) {
        swatch.classList.remove('is-disabled');
        swatch.setAttribute('data-available', 'true');
        if (radio) radio.disabled = false;
      } else {
        swatch.classList.add('is-disabled');
        swatch.setAttribute('data-available', 'false');
        if (radio) radio.disabled = true;
      }
    },

    /**
     * @param {string} setLabel - 選択中のセット値
     * @param {Object} [options] - { skipAutoSwitch: boolean }
     *        skipAutoSwitch=true の場合、1つ目フレーバーの自動切替を行わない
     *        （variant-picker 再描画と競合しないよう、再描画後の再適用時のみ切替する）
     */
    execute: function(setLabel, options) {
      options = options || {};
      const setValue = (setLabel || '').trim();
      if (!setValue) return;

      // --- 1つ目（variant-picker 内のフレーバースウォッチ） ---
      const firstFlavorSwatches = document.querySelectorAll('variant-picker .block-swatch--flavor');
      firstFlavorSwatches.forEach((swatch) => {
        const name = this._getFlavorName(swatch);
        this._applyToSwatch(swatch, FlavorAvailability.isAvailable(setValue, name));
      });

      // 1つ目で売り切れフレーバーが選択中なら、有効な先頭へ自動切替（バリアント再描画を誘発）
      if (!options.skipAutoSwitch) {
        this._autoSwitchFirstFlavorIfNeeded(firstFlavorSwatches);
      }

      // --- 2〜12つ目（property スウォッチ） ---
      const propSwatches = document.querySelectorAll('[data-flavor-set] .block-swatch--flavor');
      propSwatches.forEach((swatch) => {
        const name = this._getFlavorName(swatch);
        const available = FlavorAvailability.isAvailable(setValue, name);
        this._applyToSwatch(swatch, available);

        // 売り切れになったフレーバーが選択中なら解除
        if (!available) {
          const radio = swatch.querySelector('input[type="radio"]');
          if (radio && radio.checked) radio.checked = false;
        }
      });

      console.log('[ApplyAvailability] Applied for set:', setValue);
    },

    /**
     * 1つ目のフレーバーが現在のセットで売り切れなら、有効な先頭フレーバーを選択。
     * variant-picker のラベルクリックで再描画を誘発する。
     * @param {NodeList} swatches
     */
    _autoSwitchFirstFlavorIfNeeded: function(swatches) {
      if (this._autoSwitching) return;

      const checked = document.querySelector('variant-picker .block-swatch--flavor input[type="radio"]:checked');
      const checkedSwatch = checked ? checked.closest('.block-swatch--flavor') : null;

      // 選択中が有効ならそのまま
      if (checkedSwatch && !checkedSwatch.classList.contains('is-disabled')) return;

      // 有効な先頭フレーバーを探す
      let target = null;
      Array.prototype.some.call(swatches, (swatch) => {
        if (!swatch.classList.contains('is-disabled')) {
          target = swatch;
          return true;
        }
        return false;
      });
      if (!target) return; // 全滅時は何もしない（売り切れ表示に委ねる）

      const label = target.querySelector('label[for]');
      if (!label) return;

      this._autoSwitching = true;
      console.log('[ApplyAvailability] Auto-switching 1st flavor to available option');
      label.click(); // variant-picker の再描画 → variant:change を誘発
      // 再描画後の再入を許可（次サイクルで解除）
      setTimeout(() => { this._autoSwitching = false; }, 600);
    }
  };

  // ===============================================
  // Interface Adapter Layer - リポジトリ
  // ===============================================

  /**
   * フレーバーセットのリポジトリ（DOM操作の抽象化）
   */
  const FlavorSetRepository = {
    /**
     * フレーバーセットのDOM要素を取得
     * @param {number} setIndex - セットのインデックス
     * @returns {HTMLElement|null}
     */
    getFlavorSet: function(setIndex) {
      return document.querySelector(`.product-form__flavor-set--${setIndex}`);
    },

    /**
     * フレーバーセット内のフレーバーアイテムを取得
     * @param {HTMLElement} flavorSet - フレーバーセットの要素
     * @returns {NodeList}
     */
    getFlavorItems: function(flavorSet) {
      return flavorSet.querySelectorAll('.block-swatch--flavor');
    },

    /**
     * フレーバーセットを表示
     * @param {number} setIndex - セットのインデックス
     */
    show: function(setIndex) {
      const element = this.getFlavorSet(setIndex);
      if (element) {
        element.classList.remove('product-form__flavor-set--hidden');
      }
    },

    /**
     * フレーバーセットを非表示
     * @param {number} setIndex - セットのインデックス
     */
    hide: function(setIndex) {
      const element = this.getFlavorSet(setIndex);
      if (element) {
        element.classList.add('product-form__flavor-set--hidden');
      }
    }
  };

  /**
   * セット選択のリポジトリ
   */
  const SetSelectionRepository = {
    /**
     * 現在選択されているセットのラベルを取得
     * @returns {string}
     */
    getSelectedSetLabel: function() {
      const selectedInput = document.querySelector('input[name="option1"]:checked');
      if (selectedInput) {
        const label = selectedInput.nextElementSibling;
        return label ? label.textContent.trim() : '';
      }
      return '';
    },

    /**
     * セット選択の変更イベントを監視
     * 【修正】商品詳細ページのupdate-url対応
     * @param {Function} callback - 変更時のコールバック
     */
    onSetChange: function(callback) {
      // 通常のchangeイベント（トップページなど）
      document.addEventListener('change', function(event) {
        if (event.target.matches('input[name="option1"]')) {
          callback(event);
        }
      });

      // Shopifyのvariant:changeイベント（商品詳細ページのupdate-url対応）
      document.addEventListener('variant:change', function(event) {
        console.log('[SetSelectionRepository] variant:change event detected');
        callback(event);
      });
    }
  };

  // ===============================================
  // Framework Layer - コントローラー
  // ===============================================

  /**
   * フレーバーセット表示コントローラー
   */
  const FlavorSetController = {
    currentUrl: location.href,

    /**
     * 初期化
     */
    initialize: function() {
      this._setupSetChangeHandler();
      this._setupUrlChangeMonitor();
      this._initializeOnLoad();

      console.log('[FlavorSetController] Initialized');
    },

    /**
     * セット選択変更時のハンドラー設定
     */
    _setupSetChangeHandler: function() {
      SetSelectionRepository.onSetChange(function() {
        const setLabel = SetSelectionRepository.getSelectedSetLabel();
        const flavorCount = SetQuantity.getFlavorCount(setLabel);

        console.log(`[Controller] Set changed: "${setLabel}" → ${flavorCount} flavors`);

        // 現在のセット数より多いフレーバー選択をリセット
        ResetFlavorSelectionsUseCase.execute(flavorCount + 1, FlavorSetRepository);

        // 必要なフレーバーセットを表示
        ShowFlavorSetsUseCase.execute(flavorCount, FlavorSetRepository);

        // 選択中セットの在庫で売り切れフレーバーを無効化（即時・自動切替は再描画後に委ねる）
        ApplyAvailabilityUseCase.execute(setLabel, { skipAutoSwitch: true });

        // 表示されたフレーバーセットで自動選択
        // 【修正】遅延を500msに延長（Shopifyの在庫更新処理 / variant-picker再描画を待つ）
        setTimeout(function() {
          // 再描画後の最新DOMに対して在庫無効化を再適用（1つ目スウォッチは再描画される）
          ApplyAvailabilityUseCase.execute(SetSelectionRepository.getSelectedSetLabel());

          const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);
          requiredSets.forEach(function(setIndex) {
            AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
          });
        }, 500);
      });
    },

    /**
     * URL変更監視（バリアント変更検知）
     */
    _setupUrlChangeMonitor: function() {
      const self = this;

      SetSelectionRepository.onSetChange(function() {
        setTimeout(function() {
          if (self.currentUrl !== location.href) {
            console.log('[Controller] URL changed detected');

            // 全てのフレーバー選択をリセット
            for (let i = 2; i <= 12; i++) {
              const flavorSet = FlavorSetRepository.getFlavorSet(i);
              if (flavorSet && !flavorSet.classList.contains('product-form__flavor-set--hidden')) {
                const radios = flavorSet.querySelectorAll('input[type="radio"]');
                radios.forEach(radio => {
                  radio.checked = false;
                });
              }
            }

            self.currentUrl = location.href;

            // 再度自動選択
            const setLabel = SetSelectionRepository.getSelectedSetLabel();
            const flavorCount = SetQuantity.getFlavorCount(setLabel);
            const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);

            requiredSets.forEach(function(setIndex) {
              AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
            });
          }
        }, 400);
      });
    },

    /**
     * ページ読み込み時の初期化
     */
    _initializeOnLoad: function() {
      // 初期状態のセット数を確認
      const setLabel = SetSelectionRepository.getSelectedSetLabel();
      const flavorCount = SetQuantity.getFlavorCount(setLabel);

      if (flavorCount > 1) {
        console.log(`[Controller] Initial load: ${flavorCount} sets detected`);
        ShowFlavorSetsUseCase.execute(flavorCount, FlavorSetRepository);

        // 初期表示でも在庫無効化を適用（1つ目・2〜12つ目の両方）
        ApplyAvailabilityUseCase.execute(setLabel);

        // 【修正】遅延を500msに延長（Shopifyの在庫更新処理を待つ）
        setTimeout(function() {
          const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);
          requiredSets.forEach(function(setIndex) {
            AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
          });
        }, 500);
      } else {
        // 1袋など単品セットでも1つ目フレーバーの在庫無効化は適用
        ApplyAvailabilityUseCase.execute(setLabel);
      }
    }
  };

  // ===============================================
  // アプリケーションエントリーポイント
  // ===============================================

  /**
   * 初期化
   */
  function initialize() {
    console.log('[Product2 Set Flavor] Initializing with Clean Architecture (Vanilla JS)...');
    FlavorSetController.initialize();
    console.log('[Product2 Set Flavor] Ready.');
  }

  // DOM読み込み完了後に初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // すでに読み込み完了している場合は即座に実行
    initialize();
  }

  // グローバルに公開（デバッグ用）
  window.Product2SetFlavor = {
    Domain: {
      SetQuantity: SetQuantity,
      FlavorSelection: FlavorSelection,
      FlavorAvailability: FlavorAvailability
    },
    UseCase: {
      ShowFlavorSets: ShowFlavorSetsUseCase,
      AutoSelectFlavor: AutoSelectFlavorUseCase,
      ResetFlavorSelections: ResetFlavorSelectionsUseCase,
      ApplyAvailability: ApplyAvailabilityUseCase
    },
    Repository: {
      FlavorSet: FlavorSetRepository,
      SetSelection: SetSelectionRepository
    },
    Controller: FlavorSetController
  };

})(window);
